<script lang="ts">
	import { onMount, createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { EnergyTariff } from 'src/types/EnergyTariff';
	import { energyPriceApi } from '../../api/verivoxApi';
	import { Alert } from '@Shelly/shelly-design-system';
	import EnergyTariffCard from '../card/EnergyTariffCard.svelte';
	import VerivoxConsumerProtection from '../../verivox/VerivoxConsumerProtection.svelte';
	import { transformOfferToTariff } from '../../utils/tariffTransformers';

	const dispatch = createEventDispatcher<{ dataReady: { hasData: boolean } }>();

	export let variant: 'light' | 'dark' = 'light';
	export let layout: 'normal' | 'condensed' = 'normal';
	export let maxTariffs: number = 5;
	export let postalCode: string | undefined;
	export let locationId: string | null | undefined;
	export let totalUsage: number = 2500;
	export let subPartnerId: string = '5762';
	export let campaignId: string = '2';

	let offers: EnergyTariff[] = [];
	let isLoading = false;
	let tariffsError = '';

	const currency = '€';

	const fetchTariffs = async () => {
		let targetPostalCode = postalCode;
		let targetLocationId = locationId;

		if (!targetPostalCode || !targetLocationId) {
			const cached = energyPriceApi.getCachedLocation();
			if (cached) {
				targetPostalCode = targetPostalCode || cached.postalCode;
				targetLocationId = targetLocationId || cached.locationId;
			}
		}

		if (!targetPostalCode || !targetLocationId) {
			dispatch('dataReady', { hasData: false });
			return;
		}

		isLoading = true;

		try {
			const res = await energyPriceApi.fetchTariffs(targetPostalCode, targetLocationId);

			if (res?.isok && res?.data) {
				// Get raw offers from Verivox API response
				const rawOffers = res.data.offers || [];

				// Transform raw offers to EnergyTariff format - preserve order from API response
				offers = rawOffers
					.slice(0, maxTariffs)
					.map((offer: any) =>
						transformOfferToTariff(offer, { isSponsored: false, preferResponsiveMsp: true }),
					);

				const hasData = offers.length > 0;
				dispatch('dataReady', { hasData });
			} else {
				offers = [];
				tariffsError =
					res?.error ||
					$format('error_fetching_tariffs', {
						default: 'Tarife konnten nicht abgerufen werden. Bitte versuchen Sie es erneut.',
						locale: 'de',
					});
				dispatch('dataReady', { hasData: false });
			}
		} catch (error) {
			console.error('Error fetching tariffs:', error);
			offers = [];
			dispatch('dataReady', { hasData: false });
		} finally {
			isLoading = false;
		}
	};

	onMount(async () => {
		tariffsError = '';
		offers = [];
		isLoading = true;
		if (postalCode && locationId) {
			await fetchTariffs();
		} else {
			console.error('No postal code or location id provided');
			dispatch('dataReady', { hasData: false });
		}
	});

	$: if (postalCode && locationId) {
		fetchTariffs();
	}
</script>

{#if !isLoading && offers.length > 0}
	<div
		class="price-options energy-tariffs-modal-wrapper"
		class:variant-light={variant === 'light'}
		class:variant-dark={variant === 'dark'}
	>
		<!-- Regular Offers -->
		{#each offers as tariff, i}
			<EnergyTariffCard
				{tariff}
				{variant}
				{layout}
				{currency}
				postalCode={postalCode || ''}
				locationId={locationId || ''}
				{totalUsage}
				{subPartnerId}
				{campaignId}
			/>
		{/each}

		<!-- Verivox Consumer Protection Guidelines -->
		{#if offers.length >= 5}
			<VerivoxConsumerProtection />
		{/if}
	</div>
{:else if isLoading}
	<div class="loading-state text-center mt-l">
		<div class="spinner-border text-primary" role="status">
			<span class="visually-hidden">{$format('loading', { default: 'Loading...' })}</span>
		</div>
	</div>
{:else if tariffsError}
	<Alert type="danger">{tariffsError}</Alert>
{:else}
	<div class="no-data-state text-center mt-l">
		<span class="no-data-text">{$format('no_data', { default: 'No data available' })}</span>
	</div>
{/if}

<style>
	.price-options {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-bottom: 12px;
		width: 100%;
	}

	.price-options :global(.price-option) {
		width: 100%;
	}
</style>
